/**
 * Send feature — Repository port.
 *
 * Abstracts every side effect the Send flow needs (gas estimation, submitting
 * the transaction, polling a receipt) behind one interface so the use-cases /
 * UI never touch wagmi / RPC directly. `createSendRepository` adapts the
 * functions a host passes into `<SendFlow>` (formerly `<SendPage>` props) and
 * folds the "injected estimateGas else RPC fallback" decision in one place.
 */

import type {
  EstimateGasArgs,
  EstimateGasFn,
  GasEstimate,
  GetTransactionReceiptFn,
  SendTransactionFn,
} from "../../wallet-portfolio/types";
import { fallbackEstimateGas } from "../utils/gas-rpc";

export interface SendRepository {
  /** True when a transaction can actually be submitted. */
  readonly canSend: boolean;
  /** True when a gas estimate can be produced (injected fn or an RPC fallback). */
  canEstimateGas(rpcFallbackUrl?: string): boolean;
  /**
   * Estimate gas for the built tx args. Uses the injected `estimateGas` when
   * present, otherwise an RPC fallback against `rpcFallbackUrl`.
   * Throws when neither path is available.
   */
  estimateGas(args: EstimateGasArgs, rpcFallbackUrl?: string): Promise<GasEstimate>;
  /** Submit the transaction; resolves to the tx hash. Throws when unsupported. */
  sendTransaction(args: EstimateGasArgs): Promise<`0x${string}`>;
  /** Fetch a receipt (null until mined). Absent when the host can't poll. */
  getTransactionReceipt?: GetTransactionReceiptFn;
}

export interface SendRepositoryDeps {
  estimateGas?: EstimateGasFn;
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
}

export function createSendRepository(deps: SendRepositoryDeps): SendRepository {
  const { estimateGas, sendTransaction, getTransactionReceipt } = deps;
  return {
    canSend: Boolean(sendTransaction),
    canEstimateGas(rpcFallbackUrl) {
      return Boolean(estimateGas) || Boolean(rpcFallbackUrl);
    },
    async estimateGas(args, rpcFallbackUrl) {
      // 주입된 estimateGas 가 단일 진실원천(single source of truth) — 있으면
      // 그것만 쓴다 (develop SendPage 와 동일). 둘을 race 하면 maxFeePerGas
      // 계산식 차이(viem 은 baseFee×1.2+priority, RPC 폴백은 baseFee+priority)로
      // 표기 값이 호출마다 들쭉날쭉해진다. 주입분이 없을 때만 RPC 폴백.
      if (estimateGas) return estimateGas(args);
      if (rpcFallbackUrl) return fallbackEstimateGas(rpcFallbackUrl, args);
      throw new Error("Gas estimation is not available.");
    },
    async sendTransaction(args) {
      if (!sendTransaction) {
        throw new Error("Sending is not available for this wallet.");
      }
      return sendTransaction(args);
    },
    getTransactionReceipt,
  };
}
